import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import api from '../services/api';

export function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  useEffect(() => {
    if (!token || token.length !== 64) {
      setError('Invalid or missing reset link. Please use Forgot password again.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (token.length !== 64) {
      setError('Invalid reset link.');
      return;
    }
    if (password.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/password/reset-via-kc', {
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      if (response.data.success) {
        navigate('/login?reset=success');
        return;
      }
      setError(response.data.message || 'Could not reset password.');
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(typeof msg === 'string' ? msg : 'Could not reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#1558D6] via-[#2D7AFF] to-[#5B9AFF] p-4">
      <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8">
        <div className="flex flex-col items-center mb-6">
          <div className="size-16 rounded-2xl bg-white p-2 flex items-center justify-center shadow-lg mb-4 ring-2 ring-[#1E74FD]/20">
            <img src="/favicon.png" alt="LW Staff Portal" className="size-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 text-center">Set new password</h1>
          <p className="text-gray-600 text-center text-sm mt-2">
            Enter a new password for your staff portal account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              New password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 focus:bg-white focus:border-[#1E74FD] focus:outline-none pr-12"
                placeholder="At least 4 characters"
                disabled={loading || token.length !== 64}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm password
            </label>
            <input
              id="password_confirmation"
              type={showPassword ? 'text' : 'password'}
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 focus:bg-white focus:border-[#1E74FD] focus:outline-none"
              placeholder="Repeat password"
              disabled={loading || token.length !== 64}
              autoComplete="new-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading || token.length !== 64}
            className="w-full bg-[#1E74FD] text-white py-3 rounded-xl font-semibold hover:bg-[#1558D6] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Update password'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/login" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#1E74FD]">
            <ArrowLeft className="size-4" />
            Back to login
          </Link>
        </div>
      </div>
    </div>
  );
}
